import { LocalStorageReactive } from '@adornis/base/rxjs-extension/index.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
// this host renders Tailwind markup in its shadow root, so it needs the Tailwind flavoured base class
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import '@adornis/filter/client/x-filter-editor.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { map } from 'rxjs';
import { SgSnackbar } from '../../client/components/ui/sg-snackbar.js';
import { HealthInsuranceAPI } from '../api/HealthInsuranceAPI.js';
import { HealthInsurance } from '../entities/HealthInsurance.js';
import { showHealthInsuranceModifyDialog } from './HealthInsuranceModifyDialog.js';
import { TailwindScope } from '../../client/components/TailwindScope.js';

/**
 * @element sg-healthinsurance-dataview
 */
@customElement('sg-healthinsurance-dataview')
export class SgHealthInsuranceDataView extends ChemistryLitElement {
  @state() private _localStorage = new LocalStorageReactive('HealthInsuranceView', new View({}));
  @state() private _view = new RXController(
    this,
    this._localStorage.pipe(
      map(curView => (curView ? constructValue<View<typeof HealthInsurance>>(curView) : new View({}))),
    ),
  );

  protected columndefinitions(): ColumnDefinition<typeof HealthInsurance>[] {
    return [
      {
        customID: 'ACTIONS',
        columnName: 'Aktionen',
        columnWidth: 'min-content',
        renderHeader: () => nothing,
        isAlwaysVisible: true,
        isResizable: false,
        position: 'front',
        // legacy x-data-view cells live in a shadow root without Tailwind, see TailwindScope
        renderItem: (curIns, vE) =>
          TailwindScope.render({
            content: html`
              <div class="flex flex-row">
                <button
                  type="button"
                  class="btn btn-square btn-ghost btn-sm"
                  aria-label="Bearbeiten"
                  @click=${() => this._openModifyDialog(curIns)}
                >
                  <span class="icon-[material-symbols--edit-rounded] size-5"></span>
                </button>

                <button
                  type="button"
                  class="btn btn-square btn-ghost btn-sm"
                  aria-label="Löschen"
                  @click=${async _ => {
                    const curBez = curIns.bezeichnung;
                    await curIns
                      .remove()
                      .then(s => SgSnackbar.show(`Krankenkasse "${curBez}" wurde erfolgreich gelöscht`))
                      .catch((err: Error) =>
                        SgSnackbar.show(err.message ?? `Fehler: Beim löschen ist leider was schiefgelaufen`),
                      );
                  }}
                >
                  <span class="icon-[material-symbols--delete-rounded] size-5"></span>
                </button>
              </div>
            `,
          }),
      },
      {
        columnName: 'Betriebs-Nr.',
        fieldName: 'betriebsnummer',
        columnWidth: 'min-content',
        selectionSet: { betriebsnummer: 1 },
        getFieldMapping(entityInstance) {
          return entityInstance.betriebsnummer;
        },
      },
      {
        columnName: 'Bezeichnung',
        fieldName: 'bezeichnung',
        columnWidth: '1fr',
        selectionSet: { bezeichnung: 1 },
        getFieldMapping(entityInstance) {
          return entityInstance.bezeichnung;
        },
      },
      {
        columnName: 'Ort',
        fieldName: 'ort',
        columnWidth: '1fr',
        selectionSet: { ort: 1 },
        getFieldMapping(entityInstance) {
          return entityInstance.ort;
        },
      },
      {
        columnName: 'Art',
        fieldName: 'krkArt',
        columnWidth: 'min-content',
        selectionSet: { krkArt: 1 },
        getFieldMapping(entityInstance) {
          return entityInstance.krkArt;
        },
      },

      {
        columnName: 'gültig bis',
        fieldName: 'gueltigBis',
        columnWidth: 'min-content',
        selectionSet: { gueltigBis: 1 },
        getFieldMapping(entityInstance) {
          // a calendar day is stored at 00:00Z, so it has to be rendered pinned to UTC - date-fns'
          // format() renders in the viewer's zone and shows the previous day west of Greenwich
          return entityInstance.gueltigBis ? formatCalendarDay(entityInstance.gueltigBis, 'dd.MM.yyyy') : '/';
        },
      },
      {
        columnName: 'Rahmen-Nr.',
        fieldName: 'rahmenNr',
        columnWidth: 'min-content',
        selectionSet: { rahmenNr: 1 },
        getFieldMapping(entityInstance) {
          return entityInstance.rahmenNr;
        },
      },
    ];
  }

  protected _openModifyDialog(target?: HealthInsurance) {
    return showHealthInsuranceModifyDialog(target?._id);
  }

  override render() {
    if (!this._view.value) return nothing;

    const { dataViewOptions, filterOptions } = buildFullOptions(
      {
        class: HealthInsurance,
        operation: HealthInsuranceAPI.subscribeByFilterPaginated,
        isColumnSelectable: true,
        isColumnResortable: true,
        isColumnResizable: true,
        view: this._view.value,
        customColumns: this.columndefinitions(),
        isEntryClickable: true,
        onViewChanged: async view => this._localStorage.next(view),
      },
      {
        searchable: false,
      },
    );

    return html`
      <div class="flex flex-col gap-5">
        <div class="flex flex-col w-full shrink-0 sticky left-0 px-4">
          <div class="card card-body flex-row justify-between w-full">
            <x-filter-editor ${css({ width: '100%' })} no-collapse .options=${filterOptions}></x-filter-editor>
            <div class="flex flex-col">
              <button type="button" class="btn btn-primary" @click=${() => this._openModifyDialog()}>
                <span class="icon-[material-symbols--add-rounded] size-5"></span>
                Erstellen
              </button>
            </div>
          </div>
        </div>
        <div class="flex flex-col overflow-auto bg-base-100">
          <x-data-view .options=${dataViewOptions}></x-data-view>
        </div>
      </div>
    `;
  }
}
